/** Separates WBS+group from registered UID (e.g. "2.1.1-group1::V -101"). */
export const TASK_CODE_UID_SEP = "::";

export const parseTaskCode = (taskCode) => {
  if (!taskCode || typeof taskCode !== "string") {
    return { code: "", groupName: "", uid: "" };
  }

  let s = String(taskCode).trim();
  let uid = "";
  const sepIdx = s.lastIndexOf(TASK_CODE_UID_SEP);
  if (sepIdx >= 0) {
    uid = s.slice(sepIdx + TASK_CODE_UID_SEP.length).trim();
    s = s.slice(0, sepIdx).trim();
  }

  const dashIdx = s.indexOf("-");

  if (dashIdx < 0) {
    return { code: s, groupName: "", uid };
  }

  return {
    code: s.slice(0, dashIdx).trim(),
    groupName: s.slice(dashIdx + 1).trim(),
    uid,
  };
};

/** WBS + group without UID suffix (for lookups and display of code-group). */
export const formatBaseTaskCode = ({ code, groupName }) => {
  const c = String(code ?? "").trim();
  const gn = String(groupName ?? "").trim();
  if (!c) return "";
  return gn ? `${c}-${gn}` : c;
};

export const formatTaskCodeWithUid = ({ code, groupName, uid }) => {
  const base = formatBaseTaskCode({ code, groupName });
  const u = String(uid ?? "").trim();
  if (!base || !u) return base;
  return `${base}${TASK_CODE_UID_SEP}${u}`;
};

export const getBaseTaskCode = (taskCode) => {
  const { code, groupName } = parseTaskCode(taskCode);
  return formatBaseTaskCode({ code, groupName });
};

export const getTaskCodeOnly = (taskCode) => {
  if (!taskCode) return "";
  return parseTaskCode(taskCode).code;
};

export const getTaskCodeLevel = (taskCode) => {
  const codeOnly = getTaskCodeOnly(taskCode);

  if (!codeOnly) return 0;

  return codeOnly.split(".").filter(Boolean).length;
};

export const isTwoLevelTaskCode = (taskCode) => {
  return getTaskCodeLevel(taskCode) === 2;
};

export const isThreeLevelTaskCode = (taskCode) => {
  return getTaskCodeLevel(taskCode) === 3;
};

/** WBS deeper than a group main row (e.g. 2.1.1, 6.1.8.1); excludes 2-segment mains like 2.1. */
export const isWbsSubtaskCode = (taskCode) => {
  return getTaskCodeLevel(taskCode) > 2;
};

/** True when subtask is exactly one level under main (e.g. 1.1 → 1.1.1). */
export const isDirectThreeLevelSubtaskOf = (mainTaskCode, subTaskCode) => {
  if (!isThreeLevelTaskCode(subTaskCode)) return false;

  const { code: mainCode } = parseTaskCode(mainTaskCode);
  const { code: subCode } = parseTaskCode(subTaskCode);

  if (!mainCode || !subCode) return false;

  return subCode.startsWith(`${mainCode}.`);
};

/** Any deeper WBS under main in the same group (e.g. 6.1 → 6.1.8.1). */
export const isDescendantSubtaskOf = (mainTaskCode, subTaskCode) => {
  const { code: mainCode, groupName: mainGroup } = parseTaskCode(mainTaskCode);
  const { code: subCode, groupName: subGroup } = parseTaskCode(subTaskCode);
  if (!mainCode || !subCode) return false;
  if (mainGroup && subGroup && mainGroup !== subGroup) return false;
  if (!subCode.startsWith(`${mainCode}.`)) return false;
  return getTaskCodeLevel(subTaskCode) > getTaskCodeLevel(mainTaskCode);
};

export const getParentTaskCode = (taskCode) => {
  const codeOnly = getTaskCodeOnly(taskCode);

  if (!codeOnly) return "";

  const parts = codeOnly.split(".").filter(Boolean);

  if (parts.length < 2) return "";

  return `${parts[0]}.${parts[1]}`;
};
